import { useEffect, useState, useRef } from "react";
import { render } from "react-dom";

type Product = {
  title: string
  price_min: number,
  has_only_default_variant: boolean,
  selected_or_first_available_variant: Variant
  affirm_price: number
}

type Variant = {
  id: number
  options: [string]
  compare_at_price: string
}

(function init() {
  function loadComponent() {
    const rootElement = document.querySelector("[data-variant-radios]");
    const variantId = rootElement?.getAttribute("data-variant-id");
    
    if (rootElement)
      render(
        <VariantRadios
          variantId={variantId}
        />,
        rootElement
      );
  }

  document.addEventListener("shopify:section:load", loadComponent);
  loadComponent();
})();

function VariantRadios({ variantId }) {
  const dataContainer = document.querySelector("[data-product]");
  if (!dataContainer) return null;

  const data = JSON.parse(dataContainer.innerHTML);
  const product: Product = data?.product;
  const options_with_values = data?.options_with_values;

  if (!data) return null;

  const [options, setOptions] = useState<any[]>(product.selected_or_first_available_variant.options);

  const wigTypeDetails = useRef(null);

  useEffect(() => {
    const hiddenInput = document.querySelector('input[name="id"');
    if (hiddenInput) hiddenInput.removeAttribute('disabled');
  }, []);

  // Close disclosure on click outside
  useEffect(() => {
    const handleClick = (event) => {
      if (wigTypeDetails.current && !wigTypeDetails.current.contains(event.target)) {
        wigTypeDetails.current.open = false
      }
    };

    document.addEventListener('click', handleClick);

    return () => {
      document.removeEventListener('click', handleClick);
    };
  }, [wigTypeDetails]);

  function onVariantChange(event, i) {
    const productForm = document.getElementById(data.product_form_id);
    const hiddenInput: HTMLInputElement = productForm.querySelector('input[name="id"]');

    const updatedOptions = options.map((val, index) => {
      return i === index ? event.target.value : val; 
    });
    setOptions(updatedOptions);
    
    const currentVariant = data.variants.find((variant) => {
      return !variant.options
        .map((option, index) => {
          return updatedOptions[index] === option;
        })
        .includes(false);
    });

    if (currentVariant) {
      // Update hidden input
      hiddenInput.value = currentVariant.id;

      // Update URL
      window.history.replaceState({}, '', `${location.pathname}?variant=${currentVariant.id}`);

      const priceCompareAtContainerEls: NodeListOf<HTMLSpanElement> = document.querySelectorAll("[data-product-compare-at-container]");
      const priceEls: NodeListOf<HTMLSpanElement> = document.querySelectorAll("[data-product-price]");

      // Update current price
      priceEls.forEach(el => el.innerHTML = currentVariant.price);

      // Update sale price
      if (currentVariant.compare_at_price) {
        priceCompareAtContainerEls.forEach(el => el.style.display = "inline");
        const priceCompareAtEls = document.querySelectorAll("[data-product-compare-at-price]");
        priceCompareAtEls.forEach(el => el.innerHTML = currentVariant.compare_at_price);
      } else {
        priceCompareAtContainerEls.forEach(el => el.style.display = "none");
      }

      // Dispatch event to allow change to be consumed anywhere else in theme
      const event = new CustomEvent('variant-change', {
        detail: {
          id: currentVariant.id
        }
      });
      document.dispatchEvent(event);
    }
  }

  return (
    <div>
      {!data.product.has_only_default_variant &&
        <div className="mt-[40px]">
          {options_with_values.map((option, index) => {
            return (
              <fieldset className="mt-[16px] first:mt-0">
                <legend className="mb-[12px] text-base-m text-black/[.65] relative w-full">
                  {option.name === "Wig Type"
                    ?
                      <details ref={wigTypeDetails} className="">
                        <summary className="cursor-pointer list-none flex items-center gap-1">
                          <span className="sr-only">More information</span>
                          <span>{option.name}</span>
                          <svg className="inline" aria-hidden="true" focusable="false" width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path d="M12 22C6.47715 22 2 17.5228 2 12C2 6.47715 6.47715 2 12 2C17.5228 2 22 6.47715 22 12C22 17.5228 17.5228 22 12 22ZM11 11V17H13V11H11ZM11 7V9H13V7H11Z" fill="black"/>
                          </svg>
                        </summary>
                        <div className="z-10 text-sm leading-6 absolute bg-white p-4 top-[2rem] left-0">
                          <h2 className="font-base text-base-m normal-case mb-1">5x5 HD Closure</h2>
                          <p className="mb-2">Great for beginner-friendly looks. With an HD lace piece that spans from temple to temple, this is the perfect option for a signature glueless look without the hassle.</p>
                          <h2 className="font-base text-base-m normal-case mb-1">13x6 HD Frontal</h2>
                          <p>A more flexible full hairline look. Great for more experienced wig wearers and those looking for a fully transformed look. This HD lace piece spans from ear to ear and mimics a full natural hairline, giving you maximum parting and styling options.</p>
                          <p className="mt-2">Got more questions? <a href="/pages/virtual-consultation" target="_blank" className="underline">Book a Virtual Consultation.</a></p>
                        </div>
                      </details>
                    :
                      <span>
                        {option.name}
                      </span>
                  }
                </legend>
                {option.values.map((value, i) => {
                  return (
                    <div className="inline-block">
                      <input 
                        type="radio"
                        className="appearance-none absolute opacity-0 peer"
                        id={`${data.section_id}-${option.position}-${i}`}
                        name={option.name}
                        value={value}
                        form={data.product_form_id}
                        checked={options[index] == value}
                        onChange={(event) => onVariantChange(event, index)}
                      />
                      <label
                        htmlFor={`${data.section_id}-${option.position}-${i}`}
                        className="inline-block text-base-m mr-[8px] mb-[8px] border border-[#E2D2C7] px-[16px] py-[14px] bg-transparent peer-checked:border-black peer-checked:bg-[#F6EBE4] leading-none cursor-pointer transition-colors duration-300"

                      >
                        {value}
                      </label>
                    </div>
                  )
                })}
              </fieldset>
            )
          })}
        </div>
      }

    </div>

  )
}